/**
 * Schedule action kinds for ticket chat and the Schedules page.
 *
 * Start schedules must never be treated as step-resume timers (those disable chat).
 */

import type { TheoSchedule, TheoScheduleAction } from "../../apis/scheduleApi";

export const STRUCTURED_WORKFLOW_RESUME_ACTION = "__structured_workflow_resume";
export const STRUCTURED_WORKFLOW_START_ACTION = "__structured_workflow_start";
export const STRUCTURED_WORKFLOW_RESUME_SOURCE = "structured_workflow_resume";
export const STRUCTURED_WORKFLOW_START_SOURCE = "structured_workflow_start";
export const STRUCTURED_WORKFLOW_TIMER_KIND = "structured_workflow_timer";

export function scheduleActionConfig(action?: TheoScheduleAction | null): Record<string, any> {
  return action?.config_json && typeof action.config_json === "object" && !Array.isArray(action.config_json)
    ? action.config_json
    : {};
}

export function isStructuredWorkflowResumeAction(action?: TheoScheduleAction | null): boolean {
  const config = scheduleActionConfig(action);
  return (
    action?.action_name === STRUCTURED_WORKFLOW_RESUME_ACTION ||
    config.source === STRUCTURED_WORKFLOW_RESUME_SOURCE ||
    config.resume_kind === STRUCTURED_WORKFLOW_TIMER_KIND
  );
}

export function isStructuredWorkflowStartAction(action?: TheoScheduleAction | null): boolean {
  const config = scheduleActionConfig(action);
  return (
    action?.action_name === STRUCTURED_WORKFLOW_START_ACTION ||
    config.source === STRUCTURED_WORKFLOW_START_SOURCE
  );
}

export function structuredWorkflowResumeAction(schedule: TheoSchedule): TheoScheduleAction | null {
  return (schedule.actions || []).find(isStructuredWorkflowResumeAction) || null;
}

export function structuredWorkflowStartAction(schedule: TheoSchedule): TheoScheduleAction | null {
  return (schedule.actions || []).find(isStructuredWorkflowStartAction) || null;
}

export function isStructuredWorkflowResumeSchedule(schedule: TheoSchedule): boolean {
  return Boolean(structuredWorkflowResumeAction(schedule));
}

export function isStructuredWorkflowStartSchedule(schedule: TheoSchedule): boolean {
  return Boolean(structuredWorkflowStartAction(schedule));
}

function isPendingScheduleStatus(status?: string | null): boolean {
  const normalized = String(status || "").toLowerCase();
  return normalized === "scheduled" || normalized === "queued" || normalized === "running";
}

function isPendingActionStatus(status?: string | null): boolean {
  const normalized = String(status || "").toLowerCase();
  return normalized === "pending" || normalized === "running";
}

export function isPendingTicketSchedule(schedule: TheoSchedule): boolean {
  return isPendingScheduleStatus(schedule.status);
}

export function isPendingStructuredWorkflowResumeSchedule(schedule: TheoSchedule): boolean {
  if (!isStructuredWorkflowResumeSchedule(schedule)) return false;
  if (isPendingScheduleStatus(schedule.status)) return true;
  return (schedule.actions || []).some(
    (action) => isStructuredWorkflowResumeAction(action) && isPendingActionStatus(action?.status)
  );
}

/** Newest scheduled_for first for ticket schedule menus. */
export function sortSchedulesNewestFirst(schedules: TheoSchedule[]): TheoSchedule[] {
  return [...schedules].sort((left, right) => {
    const rightTime = Date.parse(String(right.scheduled_for || "")) || 0;
    const leftTime = Date.parse(String(left.scheduled_for || "")) || 0;
    if (rightTime !== leftTime) return rightTime - leftTime;
    return Number(right.id || 0) - Number(left.id || 0);
  });
}

export function workflowStartDisplayName(schedule: TheoSchedule): string {
  const action = structuredWorkflowStartAction(schedule);
  const config = scheduleActionConfig(action);
  const workflowName = String(config.workflow_name || "").trim();
  if (workflowName) return `Start ${workflowName}`;
  const title = String(schedule.title || "").trim();
  if (title) return title.startsWith("Start ") ? title : `Start ${title.split(":")[0].trim()}`;
  return "Start workflow";
}
